/**
 * API base URL.
 *
 * Production storefront (tropayment.com): prefers same-origin `/api` proxy.
 * If the nginx proxy returns 502, the app switches to direct api.tropayment.com
 * (persisted in sessionStorage for the browser tab).
 */
const STOREFRONT_HOSTS = [
  'tropayment.com',
  'www.tropayment.com',
];

export const PRODUCTION_API_BASE = 'https://api.tropayment.com/api';

const DIRECT_API_STORAGE_KEY = 'tropayment_api_direct';

function normalizeBase(url: string): string {
  return url.replace(/\/$/, '');
}

function isStorefrontHost(host: string): boolean {
  return STOREFRONT_HOSTS.includes(host.toLowerCase());
}

export function isDirectApiEnabled(): boolean {
  if (typeof sessionStorage === 'undefined') {
    return false;
  }

  return sessionStorage.getItem(DIRECT_API_STORAGE_KEY) === '1';
}

/** Persist direct API mode when /api proxy on storefront is broken (502). */
export function enableDirectApi(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(DIRECT_API_STORAGE_KEY, '1');
  }
}

export function getApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL as string | undefined;

  if (import.meta.env.DEV) {
    return normalizeBase(envUrl || '/api');
  }

  if (envUrl) {
    return normalizeBase(envUrl);
  }

  if (typeof window !== 'undefined' && isStorefrontHost(window.location.hostname)) {
    if (isDirectApiEnabled()) {
      return PRODUCTION_API_BASE;
    }

    return '/api';
  }

  return PRODUCTION_API_BASE;
}

/**
 * Checkout (/pay) must always use same-origin `/api` on the storefront.
 * Direct api.tropayment.com calls are blocked by CORS in the browser.
 */
export function getPayApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL as string | undefined;

  if (import.meta.env.DEV) {
    return normalizeBase(envUrl || '/api');
  }

  if (typeof window !== 'undefined' && isStorefrontHost(window.location.hostname)) {
    return '/api';
  }

  if (envUrl) {
    return normalizeBase(envUrl);
  }

  return PRODUCTION_API_BASE;
}

/** Probe /api/health on storefront; log only — do not auto-switch to direct API (often broken DNS). */
export async function bootstrapApiRouting(): Promise<void> {
  if (import.meta.env.DEV) {
    return;
  }
  if (typeof window === 'undefined') {
    return;
  }
  if (!isStorefrontHost(window.location.hostname)) {
    return;
  }
  if (isDirectApiEnabled()) {
    return;
  }

  try {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 5000);
    await fetch('/api/health', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      signal: controller.signal,
    });
    window.clearTimeout(timeout);
  } catch {
    // Keep same-origin /api — direct api.tropayment.com may be unreachable (private DNS, CORS).
  }
}

/** @deprecated use getApiBaseUrl() */
export const apiBaseUrl = getApiBaseUrl;
